import { useEffect, useState, useCallback } from 'react';
import { useSupabaseClient, useSession } from '@supabase/auth-helpers-react';
import type { UserPlan } from '../lib/types';

const FREE_USER_DAILY_LIMIT = 5;

export function useUserPlan() {
  const [plan, setPlan] = useState<UserPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dailyUsage, setDailyUsage] = useState<number>(0);
  const supabase = useSupabaseClient();
  const session = useSession();

  const fetchDailyUsage = useCallback(async (userId: string, hasMadePurchase: boolean) => {
    if (hasMadePurchase) {
      setDailyUsage(0);
      return;
    }

    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayStart = today.toISOString();

      const { count, error: countError } = await supabase
        .from('lifestyle_image_gallery')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .gte('created_at', todayStart);

      if (countError) {
        console.error('Error fetching daily usage:', countError);
        return;
      }

      setDailyUsage(count || 0);
    } catch (err) {
      console.error('Error fetching daily usage:', err);
    }
  }, [supabase]);

  const fetchPlan = useCallback(async () => {
    if (!session?.user?.id) {
      setPlan(null);
      setLoading(false);
      return;
    }

    try {
      const { data: existingPlan, error: selectError } = await supabase
        .from('user_plans')
        .select('*')
        .eq('user_id', session.user.id)
        .maybeSingle();

      if (selectError) throw selectError;

      if (existingPlan) {
        setPlan(existingPlan);
        await fetchDailyUsage(session.user.id, existingPlan.has_made_purchase === true);
      } else {
        const { data: newPlan, error: upsertError } = await supabase
          .from('user_plans')
          .upsert({ user_id: session.user.id })
          .select()
          .single();

        if (upsertError) throw upsertError;
        setPlan(newPlan);
        await fetchDailyUsage(session.user.id, false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch user plan');
    } finally {
      setLoading(false);
    }
  }, [session, supabase, fetchDailyUsage]);

  useEffect(() => {
    fetchPlan();
  }, [fetchPlan]);

  useEffect(() => {
    if (!session?.user?.id) return;

    const channelId = `user_plans_${session.user.id}_${Math.random().toString(36).slice(2)}`;
    const channel = supabase
      .channel(channelId)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'user_plans',
          filter: `user_id=eq.${session.user.id}`,
        },
        (payload) => {
          if (payload.new) {
            setPlan(payload.new as UserPlan);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [session?.user?.id, supabase]);

  const checkCredits = async (amount: number = 1): Promise<boolean> => {
    if (!session?.user?.id || !session.access_token) return false;

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/deduct-credits`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            credits: amount,
            action_type: 'background_removal',
          }),
        }
      );

      if (!response.ok) return false;

      const result = await response.json();
      if (result.success) await fetchPlan();
      return result.success === true;
    } catch (err) {
      console.error('Error checking credits:', err);
      return false;
    }
  };

  const isFreeUser = !plan?.has_made_purchase;
  const dailyRemaining = isFreeUser ? Math.max(0, FREE_USER_DAILY_LIMIT - dailyUsage) : Infinity;

  return {
    plan,
    loading,
    error,
    checkCredits,
    refreshPlan: fetchPlan,
    dailyUsage,
    dailyLimit: FREE_USER_DAILY_LIMIT,
    dailyRemaining,
    isFreeUser,
  };
}